import dayjs from 'dayjs';
import type { MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import { Navigation } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { dateIsBetween } from '@/utils/timeUtils';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import type { ModalProps } from '@/components/Modal';
import { Modal } from '@/components/Modal';
import { ProductCard } from '@/components/ProductCard';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { useRouter } from '@/hooks/useRouter';
import { getProductTranslatedName } from '@/lib/i18n';
import { paths } from '@/lib/paths';
import { generateDataTestId } from '@/lib/tests';
import { serviceMessages } from '@/messages/service';
import type { Watch } from '@/views/MyWatchesView';

import * as S from './SelectYourWatchModal.styles';

interface WatchCardProps {
  ctaText?: string;
  disabled?: boolean;
  endDate: string;
  isProcessing?: boolean;
  name: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  sku: string;
  src: string;
  startDate: string;
}

const WatchCard = ({
  ctaText,
  disabled,
  endDate,
  isProcessing,
  name,
  onClick,
  sku,
  src,
  startDate,
}: WatchCardProps) => {
  const { formatMessage } = useIntl();
  const formatEndDate = endDate && dayjs(endDate).format('DD/MM/YYYY');
  const isEffective = dateIsBetween(startDate, endDate);
  return (
    <S.WatchCardContainer data-test-id='SelectYourWatchModalWatchCard'>
      <S.ImageWrapper alignItems='center' justifyContent='center'>
        <ProductCard.Image
          alt={`${name} ${sku}`}
          data-test-id='SelectYourWatchModalWatchCardImage'
          productType='watch'
          sizes='50vw'
          src={src}
          withBackgroundPlaceholderBackground={false}
        ></ProductCard.Image>
      </S.ImageWrapper>
      <Flex
        alignItems='center'
        flex={1}
        flexDirection='column'
        gridGap='s025'
        justifyContent='flex-end'
        p={2}
      >
        <Flex alignItems='center' flex={1}>
          <Typography
            color='secondary'
            data-test-id='SelectYourWatchModalWatchCardName'
            fontWeight='bold'
            scale='h5'
            textAlign='center'
            textTransform='uppercase'
          >
            {name}
          </Typography>
        </Flex>
        <Typography
          color='secondaryGrey'
          data-test-id='SelectYourWatchModalWatchCardSku'
          scale='p3'
        >
          {sku}
        </Typography>
        <Typography data-test-id='SelectYourWatchModalWatchCardWarranty'>
          Warranty status
        </Typography>
        <Flex alignItems='center' gridGap={1}>
          <S.ValidCircle
            backgroundColor={isEffective ? 'valid' : 'error'}
            data-test-id='SelectYourWatchModalWatchCardCircle'
          ></S.ValidCircle>
          <Typography data-test-id='SelectYourWatchModalWatchCardValid'>
            {formatMessage(serviceMessages.validUntil)} {formatEndDate}
          </Typography>
        </Flex>
        <Box mt={1} width='100%'>
          <Button
            data-test-id='SelectYourWatchModalWatchCardButtonSelectWatch'
            disabled={disabled}
            isFullWidth
            isProcessing={isProcessing}
            onClick={onClick}
            style={{ whiteSpace: 'nowrap' }}
            {...generateDataTestId('button', 'selectWatch')}
          >
            {ctaText ?? formatMessage(serviceMessages.select)}
          </Button>
        </Box>
      </Flex>
    </S.WatchCardContainer>
  );
};

export type SelectYourWatchModalProps = ModalProps & {
  onHeaderClose?: () => void;
  onSelect?: (watch: Watch) => void;
  watches: Watch[];
};
export const SelectYourWatchModal = ({
  onHeaderClose,
  onSelect,
  watches,
  ...props
}: SelectYourWatchModalProps) => {
  const { push, replace } = useRouter();
  const { formatMessage } = useIntl();
  const orderAServiceFn = async () => {
    await replace(paths.service.request.url());
    onHeaderClose?.();
  };
  return (
    <Modal {...props} data-test-id='SelectYourWatchModal'>
      <Modal.Header
        data-test-id='SelectYourWatchModalHeader'
        divider
        onClose={onHeaderClose}
      >
        <SectionHeader
          data-test-id='SelectYourWatchModalSectionHeader'
          sizeDesktop={40}
          sizeMobile={34}
          text={formatMessage(serviceMessages.selectYourWatch)}
          textAlign='center'
        />
      </Modal.Header>
      <Modal.Body data-test-id='SelectYourWatchModalBody'>
        {watches.length > 0 && (
          <Flex flexDirection='column' gridGap={2} py={1}>
            <Typography textAlign='center'>
              {formatMessage(serviceMessages.selectWatchToService)}
              <br />
              {formatMessage(serviceMessages.notRegisteredNotShow)}
            </Typography>
            <Box data-test-id='SelectYourWatchModalBox'>
              <S.StyledSwiper
                centerInsufficientSlides
                centeredSlidesBounds
                modules={[Navigation]}
                navigation
                slidesPerView='auto'
                spaceBetween={12}
              >
                {watches.map((watch, index) => (
                  <SwiperSlide key={watch.id} style={{ width: '224px' }}>
                    <SelectYourWatchModal.WatchCard
                      data-test-id={`SelectYourWatchModalWatchCard-${index}`}
                      endDate={watch.end_date}
                      name={watch?.name && getProductTranslatedName(watch)}
                      onClick={() => onSelect && onSelect(watch)}
                      sku={watch.sku}
                      src={watch?.thumbnail?.url ?? ''}
                      startDate={watch.start_date}
                    ></SelectYourWatchModal.WatchCard>
                  </SwiperSlide>
                ))}
              </S.StyledSwiper>
            </Box>
          </Flex>
        )}
        {!watches.length && (
          <S.Empty
            alignItems='center'
            data-test-id='SelectYourWatchModalEmpty'
            flexDirection='column'
            gridGap={1}
            maxWidth='537px'
            py={1}
          >
            <Typography
              data-test-id='SelectYourWatchModalEmptyText'
              scale='h2'
              textAlign='center'
              textTransform='uppercase'
            >
              {formatMessage(serviceMessages.noWatchFind)}
            </Typography>
            <Typography
              data-test-id='SelectYourWatchModalEmptyTip'
              textAlign='center'
            >
              {formatMessage(serviceMessages.noWatchFindTip)}
            </Typography>
            <S.Wrapper gapDensity='none' mt={2}>
              <Col sm={6} xs={12}>
                <Button
                  data-test-id='SelectYourWatchModalEmptyButtonRegister'
                  isFullWidth
                  onClick={() => push(paths.myAccount.myWatches.asPath())}
                  variant='outlined'
                >
                  {formatMessage(serviceMessages.registerAWatch)}
                </Button>
              </Col>
              <Col sm={6} xs={12}>
                <Button
                  data-test-id='SelectYourWatchModalEmptyButtonOrder'
                  isFullWidth
                  onClick={() => orderAServiceFn()}
                >
                  {formatMessage(serviceMessages.orderAService)}
                </Button>
              </Col>
            </S.Wrapper>
          </S.Empty>
        )}
      </Modal.Body>
    </Modal>
  );
};

SelectYourWatchModal.WatchCard = WatchCard;
